import dayjs, { type Dayjs } from "dayjs"

import { DATE_FORMAT } from "../../../utils/date/date.ts"
import { shortRelativeTime } from "../../../utils/date/short-relative-time.ts"

/**
 * The API localises every date field to the account's date format, so an
 * ISO-only read turns "22/08/2026" into an invalid day.
 */
function parseLeadDate(
  value: unknown,
  userFormat?: string | null
): Dayjs | null {
  if (!value) return null

  if (typeof value === "string") {
    const iso = dayjs(value, DATE_FORMAT, true)
    if (iso.isValid()) return iso

    if (userFormat) {
      const localised = dayjs(value, userFormat, true)
      if (localised.isValid()) return localised
    }
  }

  const loose = dayjs(value as string)
  return loose.isValid() ? loose : null
}

/**
 * "Today, 3:00 PM" / "Tomorrow, 10:00 AM" / "May 20, 9:00 AM".
 *
 * A Rehash / Nurture follow-up is dated but not timed — it schedules a return,
 * not an appointment — so it reads "Rehash: Jul 15".
 */
function formatFollowUp(
  date: unknown,
  time?: string | null,
  {
    isRehash = false,
    instant,
    now = dayjs(),
    userFormat
  }: {
    isRehash?: boolean
    /** The linked event's own instant, which the calendar renders too. */
    instant?: string | null
    now?: Dayjs
    userFormat?: string | null
  } = {}
): string {
  // The naive date/time pair is a wall clock written in the follow-up owner's
  // zone. Rendering it raw disagreed with the calendar, which resolves the same
  // meeting in the viewer's zone — so the instant wins wherever we have one.
  const fromInstant = instant ? dayjs(instant).tz() : null
  const at = fromInstant?.isValid()
    ? fromInstant
    : parseLeadDate(date, userFormat)
  if (!at) return ""

  if (isRehash) return `Rehash: ${at.format("MMM D")}`

  const day = at.isSame(now, "day")
    ? "Today"
    : at.isSame(now.add(1, "day"), "day")
      ? "Tomorrow"
      : at.format("MMM D")

  const clock = fromInstant?.isValid()
    ? fromInstant.format("h:mm A")
    : formatFollowUpTime(time)
  return clock ? `${day}, ${clock}` : day
}

/** The API stores follow_up_time as an "HH:mm" string. */
function formatFollowUpTime(time?: string | null): string {
  if (!time) return ""
  const parsed = dayjs(String(time), "HH:mm")
  return parsed.isValid() ? parsed.format("h:mm A") : String(time)
}

function isFollowUpOverdue(
  date: unknown,
  now: Dayjs = dayjs(),
  userFormat?: string | null
): boolean {
  const at = parseLeadDate(date, userFormat)
  return !!at && at.isBefore(now, "day")
}

/**
 * The four follow-up readings the client's icon pack colours: a rehash return,
 * a follow-up due now or already missed, one landing tomorrow, and anything
 * further out. Overdue folds into `due` — both demand attention today, and the
 * cell says "Overdue" in words underneath.
 */
type FollowUpState = "rehash" | "due" | "upcoming" | "scheduled"

function followUpState(
  date: unknown,
  {
    isRehash = false,
    now = dayjs(),
    userFormat
  }: { isRehash?: boolean; now?: Dayjs; userFormat?: string | null } = {}
): FollowUpState | null {
  if (isRehash) return "rehash"
  const at = parseLeadDate(date, userFormat)
  if (!at) return null

  if (at.isBefore(now, "day") || at.isSame(now, "day")) return "due"
  if (at.isSame(now.add(1, "day"), "day")) return "upcoming"
  return "scheduled"
}

/** "Michael Sanderson" → "Michael S." — the table shows one line per owner. */
function abbreviateName(
  firstName?: string | null,
  lastName?: string | null
): string {
  const first = (firstName || "").trim()
  const last = (lastName || "").trim()
  if (!first) return last
  if (!last) return first
  return `${first} ${last.charAt(0)}.`
}

export {
  abbreviateName,
  followUpState,
  parseLeadDate,
  formatFollowUp,
  formatFollowUpTime,
  isFollowUpOverdue,
  shortRelativeTime,
  type FollowUpState
}
